import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import {
  FiFilter,
  FiList,
  FiMapPin,
  FiMaximize2,
  FiNavigation,
  FiRefreshCw,
  FiSearch,
  FiUsers,
  FiX,
} from 'react-icons/fi';
import EmployeeMap from '../../components/location/EmployeeMap';
import { API_ENDPOINTS } from '../../utils/api';
import { useLiveLocationPing } from '../../hooks/useLiveLocationPing';
import {
  fetchCurrentLocation,
  getStoredLocation,
  LIVE_LOCATION_EVENT,
} from '../../utils/locationPermissionUtils';
import {
  buildLocationStatsFromMap,
  formatLastUpdate,
  getDepartmentOptions,
  getMapCenter,
  getOnlineStatus,
  prepareEmployeesForMap,
  resolveAvatarUrl,
  STATUS_META,
  uniqueEmployeesByIdentity,
} from '../../utils/locationUtils';
import EmployeeMarkerDetails from '../../components/location/EmployeeMarkerDetails';
import LocationName from '../../components/location/LocationName';
import { prefetchLocationNames } from '../../utils/locationSearchUtils';
import './EmployeeLocation.css';

const StatTile = ({ label, value, tone = 'navy' }) => {
  const tones = {
    navy: 'text-[#1a365d]',
    teal: 'text-teal-700',
    blue: 'text-blue-700',
    amber: 'text-amber-700',
    rose: 'text-rose-700',
    emerald: 'text-emerald-700',
  };
  return (
    <div className="rounded-xl border border-slate-200 bg-white px-3 py-2">
      <p className="text-[10px] uppercase tracking-wide text-slate-500">{label}</p>
      <p className={`text-xl font-bold ${tones[tone]}`}>{value}</p>
    </div>
  );
};

const EmployeeListPanel = ({
  loading,
  filteredEmployees,
  search,
  setSearch,
  department,
  setDepartment,
  departments,
  statusFilter,
  setStatusFilter,
  selectedId,
  setSelectedId,
  currentUserId,
  compact = false,
}) => (
  <>
    <div className={`border-b border-slate-100 space-y-3 ${compact ? 'p-3' : 'p-4'}`}>
      <div className="relative">
        <FiSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search employee…"
          className="w-full rounded-xl border border-slate-200 pl-9 pr-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500/30 focus:border-teal-400"
        />
      </div>
      <div className="grid grid-cols-2 gap-2">
        <div className="relative">
          <FiFilter className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400 w-3.5 h-3.5" />
          <select
            value={department}
            onChange={(e) => setDepartment(e.target.value)}
            className="w-full rounded-lg border border-slate-200 pl-8 pr-2 py-2 text-xs focus:outline-none focus:ring-2 focus:ring-teal-500/30"
          >
            {departments.map((dept) => (
              <option key={dept} value={dept}>
                {dept === 'all' ? 'All departments' : dept}
              </option>
            ))}
          </select>
        </div>
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="w-full rounded-lg border border-slate-200 px-2 py-2 text-xs focus:outline-none focus:ring-2 focus:ring-teal-500/30"
        >
          <option value="all">All statuses</option>
          <option value="online">Online</option>
          <option value="offline">Offline</option>
          {Object.entries(STATUS_META).map(([key, meta]) => (
            <option key={key} value={key}>
              {meta.label}
            </option>
          ))}
        </select>
      </div>
    </div>

    <div className="flex-1 overflow-y-auto divide-y divide-slate-100">
      {loading && filteredEmployees.length === 0 ? (
        <div className="p-8 text-center text-sm text-slate-500">Loading employees…</div>
      ) : filteredEmployees.length === 0 ? (
        <div className="p-8 text-center text-sm text-slate-500">No employees match your filters.</div>
      ) : (
        filteredEmployees.map((emp) => {
          const meta = STATUS_META[emp.status] || STATUS_META.offline;
          const online = getOnlineStatus(emp);
          const isSelected = emp.userId === selectedId;
          const isMe =
            currentUserId &&
            (emp.userId === currentUserId || emp.userId?.toString() === currentUserId?.toString());

          return (
            <button
              key={emp.userId}
              type="button"
              onClick={() => setSelectedId(isSelected ? null : emp.userId)}
              className={`w-full text-left p-3 flex items-start gap-3 hover:bg-slate-50 transition-colors ${
                isSelected ? 'bg-teal-50/70 border-l-4 border-teal-500' : ''
              }`}
            >
              <div className="relative shrink-0">
                <img
                  src={resolveAvatarUrl(emp.profilePic, emp.name)}
                  alt={emp.name}
                  title={emp.name}
                  className="w-10 h-10 rounded-full object-cover border border-slate-200"
                />
                <span
                  className={`absolute -bottom-0.5 -right-0.5 w-3 h-3 rounded-full border-2 border-white ${online.dot}`}
                  title={online.label}
                />
              </div>
              <div className="min-w-0 flex-1">
                <div className="flex items-center justify-between gap-2">
                  <p className="font-semibold text-sm text-slate-800 truncate" title={emp.name}>
                    {emp.name}
                    {isMe ? (
                      <span className="ml-1 text-[10px] font-bold text-teal-600">(You)</span>
                    ) : null}
                  </p>
                  <span className={`shrink-0 px-2 py-0.5 rounded-full text-[10px] font-semibold ${online.badge}`}>
                    {online.label}
                  </span>
                </div>
                <p className="text-xs text-slate-500 truncate">
                  {[emp.position, emp.department].filter(Boolean).join(' · ')}
                </p>
                <div className="flex items-center gap-2 mt-1 flex-wrap">
                  <span className={`px-1.5 py-0.5 rounded-full text-[10px] font-semibold ${meta.badge}`}>
                    {meta.label}
                  </span>
                  {emp.hasLocation ? (
                    <span className="text-[10px] text-teal-600 font-medium">
                      {emp.isLive
                        ? 'Live GPS'
                        : emp.isApproximateLocation || emp.locationSource === 'last-known'
                          ? 'Last known'
                          : 'Check-in GPS'}
                    </span>
                  ) : online.isOnline ? (
                    <span className="text-[10px] text-amber-600 font-medium">Awaiting GPS</span>
                  ) : (
                    <span className="text-[10px] text-slate-400">No GPS yet</span>
                  )}
                </div>
                {emp.hasLocation ? (
                  <p className="text-[11px] text-slate-500 mt-1 flex items-start gap-1">
                    <FiNavigation className="w-3 h-3 shrink-0 mt-0.5" />
                    <LocationName
                      lat={emp.latitude}
                      lng={emp.longitude}
                      approximate={emp.isApproximateLocation}
                      accuracy={emp.accuracy}
                      className="leading-snug"
                      showCoords
                    />
                  </p>
                ) : null}
                {emp.email ? (
                  <p className="text-[11px] text-slate-400 truncate">{emp.email}</p>
                ) : null}
                <p className="text-[11px] text-slate-400 mt-0.5">{formatLastUpdate(emp.lastUpdate)}</p>
              </div>
            </button>
          );
        })
      )}
    </div>
  </>
);

const SelectedEmployeeBar = ({ employee }) => {
  if (!employee) return null;

  return (
    <div className="rounded-xl border border-slate-200 bg-white p-3">
      <EmployeeMarkerDetails employee={employee} />
    </div>
  );
};

const EmployeeLocation = () => {
  const [employees, setEmployees] = useState([]);
  const [offices, setOffices] = useState([]);
  const [currentUserId, setCurrentUserId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [department, setDepartment] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [selectedId, setSelectedId] = useState(null);
  const [updatedAt, setUpdatedAt] = useState(null);
  const [isFullView, setIsFullView] = useState(false);
  const [showFullViewSidebar, setShowFullViewSidebar] = useState(true);
  const [mapResizeToken, setMapResizeToken] = useState(0);

  useLiveLocationPing();

  const authHeaders = () => ({
    Authorization: `Bearer ${localStorage.getItem('token')}`,
  });

  const loadLocations = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(API_ENDPOINTS.getEmployeeLocations, {
        headers: authHeaders(),
      });

      let list = uniqueEmployeesByIdentity(data.employees || []);
      const meId = data.currentUserId;
      setOffices(Array.isArray(data.offices) ? data.offices : []);

      list = list.map((emp) => {
        const isMe = meId && (emp.userId === meId || emp.userId?.toString() === meId?.toString());
        if (!isMe) return emp;
        return {
          ...emp,
          isOnline: true,
          isSessionOnline: true,
          status:
            emp.status === 'offline' || emp.status === 'checked-out' || emp.status === 'no-location'
              ? emp.hasLocation
                ? 'field'
                : 'in-office'
              : emp.status,
        };
      });

      setEmployees((prev) =>
        list.map((emp) => {
          const prevEmp = prev.find(
            (item) => item.userId === emp.userId || item.userId?.toString() === emp.userId?.toString()
          );
          if (emp.hasLocation || !prevEmp?.hasLocation) return emp;
          return {
            ...emp,
            latitude: prevEmp.latitude,
            longitude: prevEmp.longitude,
            hasLocation: true,
            isLive: true,
            locationSource: prevEmp.locationSource || 'live',
            isApproximateLocation: false,
          };
        })
      );
      setCurrentUserId(meId || null);
      setUpdatedAt(data.updatedAt || new Date().toISOString());
    } catch (err) {
      console.error('Failed to load employee locations', err);
      setEmployees([]);
      setOffices([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadLocations();
    const interval = setInterval(loadLocations, 15000);
    return () => clearInterval(interval);
  }, [loadLocations]);

  useEffect(() => {
    const onLiveLocation = (event) => {
      const { lat, lng } = event.detail || {};
      const nextLat = Number(lat);
      const nextLng = Number(lng);
      if (!Number.isFinite(nextLat) || !Number.isFinite(nextLng)) return;

      setEmployees((list) =>
        list.map((emp) => {
          const isMe =
            currentUserId &&
            (emp.userId === currentUserId || emp.userId?.toString() === currentUserId?.toString());
          if (!isMe) return emp;
          return {
            ...emp,
            latitude: nextLat,
            longitude: nextLng,
            hasLocation: true,
            isApproximateLocation: false,
            accuracy: Number.isFinite(Number(event.detail?.accuracy))
              ? Number(event.detail.accuracy)
              : emp.accuracy,
            isOnline: true,
            isLive: true,
            status: emp.status === 'offline' || emp.status === 'no-location' ? 'field' : emp.status,
          };
        })
      );
    };

    window.addEventListener(LIVE_LOCATION_EVENT, onLiveLocation);
    return () => window.removeEventListener(LIVE_LOCATION_EVENT, onLiveLocation);
  }, [currentUserId]);

  const shareMyLocation = async () => {
    const token = localStorage.getItem('token');
    try {
      const coords = await fetchCurrentLocation({ silent: false });
      const [lat, lng] = coords.split(',').map(Number);
      if (!Number.isFinite(lat) || !Number.isFinite(lng)) return;
      const stored = getStoredLocation();
      const accuracy = Number.isFinite(stored?.accuracy) ? stored.accuracy : undefined;

      if (token) {
        try {
          await axios.post(
            API_ENDPOINTS.pingLiveLocation,
            { latitude: lat, longitude: lng },
            { headers: { Authorization: `Bearer ${token}` } }
          );
        } catch (err) {
          console.warn('Location ping failed', err?.response?.status || err?.message);
        }
      }

      setEmployees((list) =>
        list.map((emp) => {
          const isMe =
            currentUserId &&
            (emp.userId === currentUserId || emp.userId?.toString() === currentUserId?.toString());
          if (!isMe) return emp;
          return {
            ...emp,
            latitude: lat,
            longitude: lng,
            hasLocation: true,
            isApproximateLocation: false,
            accuracy,
            isOnline: true,
            isLive: true,
            status: 'field',
          };
        })
      );
      setSelectedId(currentUserId);
    } catch (err) {
      window.alert(
        'Location is blocked. Click the lock icon in the address bar → Site settings → Location → Allow, then try again.'
      );
    }
  };

  useEffect(() => {
    if (!isFullView) return undefined;

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    const onKeyDown = (event) => {
      if (event.key === 'Escape') {
        setIsFullView(false);
      }
    };

    window.addEventListener('keydown', onKeyDown);
    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener('keydown', onKeyDown);
    };
  }, [isFullView]);

  useEffect(() => {
    setMapResizeToken((token) => token + 1);
  }, [isFullView, showFullViewSidebar]);

  const departments = useMemo(() => getDepartmentOptions(employees), [employees]);
  const mapCenter = useMemo(() => getMapCenter(offices, employees), [offices, employees]);

  const filteredEmployees = useMemo(() => {
    const q = search.trim().toLowerCase();
    return employees.filter((emp) => {
      if (department !== 'all' && emp.department !== department) return false;
      if (statusFilter === 'online' && !getOnlineStatus(emp).isOnline) return false;
      if (statusFilter === 'offline' && getOnlineStatus(emp).isOnline) return false;
      if (statusFilter !== 'all' && statusFilter !== 'online' && statusFilter !== 'offline') {
        if (emp.status !== statusFilter) return false;
      }
      if (!q) return true;
      return (
        emp.name?.toLowerCase().includes(q) ||
        emp.email?.toLowerCase().includes(q) ||
        emp.employeeId?.toLowerCase().includes(q) ||
        emp.department?.toLowerCase().includes(q)
      );
    });
  }, [employees, search, department, statusFilter]);

  const mapEmployees = useMemo(
    () => prepareEmployeesForMap(filteredEmployees),
    [filteredEmployees]
  );

  const stats = useMemo(
    () => buildLocationStatsFromMap(employees, mapEmployees),
    [employees, mapEmployees]
  );

  useEffect(() => {
    prefetchLocationNames(mapEmployees);
  }, [mapEmployees]);

  const selectedEmployee = useMemo(() => {
    if (!selectedId) return null;
    return (
      mapEmployees.find((e) => e.userId === selectedId) ||
      filteredEmployees.find((e) => e.userId === selectedId) ||
      null
    );
  }, [selectedId, mapEmployees, filteredEmployees]);

  const myProfile = useMemo(
    () =>
      employees.find(
        (emp) =>
          currentUserId &&
          (emp.userId === currentUserId || emp.userId?.toString() === currentUserId?.toString())
      ) || null,
    [employees, currentUserId]
  );

  const mapProps = {
    employees: mapEmployees,
    offices,
    selectedId,
    onSelectEmployee: setSelectedId,
    center: mapCenter,
    resizeToken: mapResizeToken,
    isFullView,
    onToggleFullView: () => setIsFullView((value) => !value),
  };

  const listProps = {
    loading,
    filteredEmployees,
    search,
    setSearch,
    department,
    setDepartment,
    departments,
    statusFilter,
    setStatusFilter,
    selectedId,
    setSelectedId,
    currentUserId,
  };

  return (
    <>
      {isFullView && (
        <div className="employee-location-fullview">
          <header className="employee-location-fullview__header">
            <div className="flex items-center gap-3 min-w-0">
              <FiMapPin className="text-teal-300 shrink-0" />
              <div className="min-w-0">
                <p className="font-bold truncate">Live Employee Location — Full View</p>
                <p className="text-xs text-slate-300 truncate">
                  {mapEmployees.length} on map · {stats.online} online · Press Esc to exit
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <button
                type="button"
                className="employee-location-fullview__toggle-sidebar"
                onClick={() => setShowFullViewSidebar((value) => !value)}
              >
                <FiList />
                {showFullViewSidebar ? 'Hide list' : 'Show list'}
              </button>
              <button
                type="button"
                onClick={shareMyLocation}
                className="employee-location-fullview__toggle-sidebar"
              >
                <FiMapPin />
                Show my location
              </button>
              <button
                type="button"
                onClick={loadLocations}
                disabled={loading}
                className="employee-location-fullview__toggle-sidebar"
              >
                <FiRefreshCw className={loading ? 'animate-spin' : ''} />
                Refresh
              </button>
              <button
                type="button"
                className="employee-location-fullview__toggle-sidebar"
                onClick={() => setIsFullView(false)}
              >
                <FiX />
                Exit
              </button>
            </div>
          </header>

          <div className="employee-location-fullview__body">
            <aside
              className={`employee-location-fullview__sidebar ${
                showFullViewSidebar ? '' : 'is-collapsed'
              }`}
            >
              <EmployeeListPanel {...listProps} compact />
            </aside>

            <main className="employee-location-fullview__map">
              <EmployeeMap {...mapProps} fullView />
            </main>
          </div>

          {selectedEmployee && (
            <footer className="employee-location-fullview__footer">
              <SelectedEmployeeBar employee={selectedEmployee} />
            </footer>
          )}
        </div>
      )}

      <div className={`min-h-screen bg-gradient-to-br from-slate-100 via-teal-50/30 to-slate-100 p-4 md:p-6 ${isFullView ? 'hidden' : ''}`}>
        <div className="max-w-[1600px] mx-auto space-y-4">
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div>
              <h1 className="text-2xl md:text-3xl font-bold text-[#1a365d] flex items-center gap-3">
                <FiMapPin className="text-teal-600" />
                Live Employee Location
              </h1>
              <p className="text-slate-600 mt-1">
                GPS-based live map — every employee with a device ping or check-in pin appears here
              </p>
            </div>
            <div className="flex items-center gap-2">
              {updatedAt ? (
                <span className="text-xs text-slate-500 hidden sm:inline">
                  Updated {formatLastUpdate(updatedAt)}
                </span>
              ) : null}
              <button
                type="button"
                onClick={shareMyLocation}
                className="inline-flex items-center gap-2 rounded-xl bg-teal-600 px-4 py-2 text-sm font-semibold text-white hover:bg-teal-700 transition-colors"
              >
                <FiMapPin />
                Show my location
              </button>
              <button
                type="button"
                onClick={() => setIsFullView(true)}
                className="inline-flex items-center gap-2 rounded-xl border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-[#1a365d] hover:bg-slate-50 transition-colors"
              >
                <FiMaximize2 />
                Full view
              </button>
              <button
                type="button"
                onClick={loadLocations}
                disabled={loading}
                className="inline-flex items-center gap-2 rounded-xl bg-[#1a365d] px-4 py-2 text-sm font-semibold text-white hover:bg-[#152c4d] disabled:opacity-60 transition-colors"
              >
                <FiRefreshCw className={loading ? 'animate-spin' : ''} />
                Refresh
              </button>
            </div>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-7 gap-3">
            <StatTile label="Employees" value={stats.total} />
            <StatTile label="Online" value={stats.online} tone="emerald" />
            <StatTile label="Offline" value={stats.offline} tone="rose" />
            <StatTile label="On map" value={stats.onMap} tone="teal" />
            <StatTile label="Moving" value={stats.moving} tone="blue" />
            <StatTile label="In office" value={stats.inOffice} tone="teal" />
            <StatTile label="On field" value={stats.field} tone="amber" />
          </div>

          <div className="grid grid-cols-1 xl:grid-cols-[360px_1fr] gap-4 min-h-[620px]">
            <div className="rounded-2xl border border-slate-200 bg-white shadow-sm flex flex-col overflow-hidden max-h-[720px] xl:max-h-none">
              <EmployeeListPanel {...listProps} />
            </div>

            <div className="rounded-2xl border border-slate-200 bg-white shadow-sm p-3 md:p-4 flex flex-col min-h-[420px] xl:min-h-[620px]">
              <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
                <div className="flex items-center gap-2 text-sm font-semibold text-[#1a365d]">
                  <FiUsers />
                  {mapEmployees.length} profile{mapEmployees.length !== 1 ? 's' : ''} on map
                </div>
                <div className="flex flex-wrap gap-2 text-[11px]">
                  {Object.entries(STATUS_META).slice(0, 5).map(([key, meta]) => (
                    <span key={key} className="inline-flex items-center gap-1 text-slate-600">
                      <span className={`w-2 h-2 rounded-full ${meta.color}`} />
                      {meta.label}
                    </span>
                  ))}
                </div>
              </div>

              <div className="flex-1 min-h-[360px] relative">
                <EmployeeMap {...mapProps} />

                {mapEmployees.length === 0 && !loading ? (
                  <div className="absolute inset-x-3 bottom-3 z-[1100] rounded-xl border border-slate-200 bg-white/95 px-4 py-3 text-sm text-slate-600 shadow-lg">
                    No GPS points yet. Locations appear when employees allow location sharing or check in with GPS.
                    <button
                      type="button"
                      onClick={shareMyLocation}
                      className="mt-2 inline-flex items-center gap-2 rounded-lg bg-[#1a365d] px-3 py-1.5 text-xs font-bold text-white"
                    >
                      <FiMapPin />
                      Share my location
                    </button>
                  </div>
                ) : null}

                {!myProfile?.hasLocation && mapEmployees.length > 0 ? (
                  <div className="absolute left-3 right-3 bottom-3 z-[1100] flex flex-wrap items-center justify-between gap-2 rounded-xl border border-amber-200 bg-amber-50/95 px-3 py-2 text-xs text-amber-900 shadow-md">
                    <span>Your GPS is not on the map yet. Others are still visible.</span>
                    <button
                      type="button"
                      onClick={shareMyLocation}
                      className="inline-flex items-center gap-1 rounded-lg bg-teal-600 px-3 py-1.5 font-bold text-white"
                    >
                      <FiMapPin />
                      Share my location
                    </button>
                  </div>
                ) : null}
              </div>

              {selectedEmployee && (
                <div className="mt-3 rounded-xl border border-slate-200 bg-slate-50 p-3">
                  <SelectedEmployeeBar employee={selectedEmployee} />
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default EmployeeLocation;
